import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE } from '../config';

export const HANDBOOKS_CACHE_KEY = 'handbooks_cache';
export const HANDBOOKS_CACHE_TIME_KEY = 'handbooks_cache_time';
/** Версия справочников (id последней записи журнала на сервере), с которой сохранён кэш. */
export const HANDBOOKS_CACHE_VERSION_KEY = 'handbooks_cache_version';
const MAX_CACHE_AGE = 24 * 60 * 60 * 1000; // 24 часа

const readCache = () => {
  try {
    const cached = localStorage.getItem(HANDBOOKS_CACHE_KEY);
    const cacheTime = localStorage.getItem(HANDBOOKS_CACHE_TIME_KEY);
    if (!cached || !cacheTime) return null;
    if (Date.now() - parseInt(cacheTime, 10) >= MAX_CACHE_AGE) return null;
    return { handbooks: JSON.parse(cached), version: localStorage.getItem(HANDBOOKS_CACHE_VERSION_KEY) };
  } catch {
    return null;
  }
};

const writeCache = (handbooksData, version) => {
  try {
    localStorage.setItem(HANDBOOKS_CACHE_KEY, JSON.stringify(handbooksData));
    localStorage.setItem(HANDBOOKS_CACHE_TIME_KEY, Date.now().toString());
    if (version !== undefined && version !== null) {
      localStorage.setItem(HANDBOOKS_CACHE_VERSION_KEY, String(version));
    } else {
      localStorage.removeItem(HANDBOOKS_CACHE_VERSION_KEY);
    }
  } catch {
    // Переполненное или недоступное хранилище — работаем без кэша.
  }
};

/** Текущая версия справочников на сервере или null, если узнать не удалось. */
export const fetchHandbooksVersion = async () => {
  try {
    const response = await fetch(`${API_BASE}/all-guide/version/`);
    if (!response.ok) return null;
    const data = await response.json();
    return data && data.version !== undefined && data.version !== null ? String(data.version) : null;
  } catch {
    return null;
  }
};

export const useHandbooks = () => {
  const [handbooks, setHandbooks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  /** Скачивает справочники заново, не показывая заставку; при ошибке остаётся кэш. */
  const refreshInBackground = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/all-guide/`);
      if (!response.ok) return;
      const data = await response.json();
      const handbooksData = data.handbooks || [];
      if (!mountedRef.current || handbooksData.length === 0) return;
      setHandbooks(handbooksData);
      writeCache(handbooksData, data.version);
    } catch {
      // Сеть недоступна — продолжаем со справочниками из кэша.
    }
  }, []);

  const loadHandbooks = useCallback(async (forceReload = false) => {
    setIsLoading(true);
    setError(null);

    // Добавляем минимальную задержку для лучшего UX
    const startTime = Date.now();
    const minLoadingTime = 500; // 500ms минимальное время показа лоадера

    try {
      // Кэш показываем сразу, а потом сверяем его версию с сервером: справочники
      // поменяли (добавили справочник, переименовали записи) — скачиваем заново,
      // не дожидаясь, пока кэш устареет через сутки.
      if (!forceReload) {
        const cache = readCache();
        if (cache) {
          setHandbooks(cache.handbooks);
          setIsLoading(false);
          const serverVersion = await fetchHandbooksVersion();
          if (serverVersion !== null && serverVersion !== cache.version && mountedRef.current) {
            // Тихо, без заставки «Загрузка справочников…»: она закрыла бы уже открытую карточку.
            await refreshInBackground();
          }
          return;
        }
      }

      // Загружаем данные
      const response = await fetch(`${API_BASE}/all-guide/`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const handbooksData = data.handbooks || [];

      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

      setTimeout(() => {
        if (!mountedRef.current) return;
        setHandbooks(handbooksData);
        writeCache(handbooksData, data.version);
        setIsLoading(false);
      }, remainingTime);

    } catch (err) {
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

      setTimeout(() => {
        if (!mountedRef.current) return;
        setError(err.message);
        setIsLoading(false);
      }, remainingTime);
    }
  }, [refreshInBackground]);

  useEffect(() => {
    loadHandbooks();
  }, [loadHandbooks]);

  return {
    handbooks,
    isLoading,
    error,
    loadHandbooks
  };
};
